/**
 * API 키 관리 서비스 (관리자 전용)
 */
import { getToken, API_BASE_URL } from './api';

// 공통 헤더
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getToken()}`
});

/**
 * 프로바이더 목록 조회
 */
export const getProviders = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/providers`, {
    headers: getHeaders()
  });
  return response.json();
};

/**
 * API 키 목록 조회
 */
export const getApiKeys = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys`, {
    headers: getHeaders()
  });
  return response.json();
};

/**
 * API 키 추가
 */
export const addApiKey = async (provider, label, apiKey, model = null) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ provider, label, apiKey, model })
  });
  return response.json();
};

/**
 * API 키 수정
 */
export const updateApiKey = async (id, updates) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(updates)
  });
  return response.json();
};

/**
 * API 키 삭제
 */
export const deleteApiKey = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return response.json();
};

/**
 * API 키 테스트
 */
export const testApiKey = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/${id}/test`, {
    method: 'POST',
    headers: getHeaders()
  });
  return response.json();
};

/**
 * API 키 사용 로그 조회
 */
export const getApiKeyLogs = async (id, limit = 50, offset = 0) => {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/api-keys/${id}/logs?limit=${limit}&offset=${offset}`,
    { headers: getHeaders() }
  );
  return response.json();
};

/**
 * 사용 통계 조회
 */
export const getApiKeyStats = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/stats/summary`, {
    headers: getHeaders()
  });
  return response.json();
};

/**
 * API 키 잔액/크레딧 조회
 */
export const getApiKeyBalance = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/${id}/balance`, {
    headers: getHeaders()
  });
  return response.json();
};

/**
 * 일별 사용량 통계 조회
 */
export const getDailyStats = async (days = 30) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/stats/daily?days=${days}`, {
    headers: getHeaders()
  });
  return response.json();
};

// ============================================
// 프로젝트 관리 API
// ============================================

/**
 * 프로젝트 목록 조회
 */
export const getProjects = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/projects`, {
    headers: getHeaders()
  });
  return response.json();
};

/**
 * 프로젝트 추가
 */
export const addProject = async (code, name, subdomain, description) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/projects`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ code, name, subdomain, description })
  });
  return response.json();
};

/**
 * 프로젝트 수정
 */
export const updateProject = async (id, updates) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/projects/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(updates)
  });
  return response.json();
};

/**
 * 프로젝트 삭제
 */
export const deleteProject = async (id) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/projects/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return response.json();
};

/**
 * 키에 프로젝트 할당
 */
export const assignProject = async (keyId, projectId, options = {}) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/${keyId}/assign-project`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      projectId,
      dailyQuota: options.dailyQuota || 0,
      monthlyQuota: options.monthlyQuota || 0,
      isPrimary: options.isPrimary || false
    })
  });
  return response.json();
};

/**
 * 키에서 프로젝트 할당 해제
 */
export const unassignProject = async (keyId, projectId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/${keyId}/unassign-project/${projectId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return response.json();
};

/**
 * 키의 할당된 프로젝트 목록 조회
 */
export const getKeyProjects = async (keyId) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/${keyId}/projects`, {
    headers: getHeaders()
  });
  return response.json();
};

/**
 * 프로젝트별 키 조회
 */
export const getKeysByProject = async (projectCode) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/api-keys/by-project/${projectCode}`, {
    headers: getHeaders()
  });
  return response.json();
};

export default {
  getProviders,
  getApiKeys,
  addApiKey,
  updateApiKey,
  deleteApiKey,
  testApiKey,
  getApiKeyLogs,
  getApiKeyStats,
  getApiKeyBalance,
  getDailyStats,
  // 프로젝트 관련
  getProjects,
  addProject,
  updateProject,
  deleteProject,
  assignProject,
  unassignProject,
  getKeyProjects,
  getKeysByProject
};
